import axios from 'axios';
import './Food/FoodTopic.css';
import { CommentSection } from './Utils/CommentSectionUtility';
import { IsHtml } from './Utils/Utility';
import { enableScroll } from './Scroll';
import { useParams } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { FetchCountryIso, ParseCoordinates } from './Utils/CountryUtility';
import { LocalClock } from './Utils/TimezoneUtility';
import { FetchFood, FoodPost } from './Food/FoodRequests';
import { dietary, foodTypes } from './Food/FoodLayout';
import { UpdateMapView, HandleMarkerItemClick, MemoizedMarker, CreateCustomIcon, MapGestureControl } from './LeafletMap/LeafletMapLayout';
import { MapContainer, TileLayer } from 'react-leaflet';
import { Helmet } from 'react-helmet';
import 'leaflet/dist/leaflet.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { MultiSlider } from './MultiSlider';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface FoodTopicLocationState {
  selectedItemCursor?: string;
  selectedItemCursorToken?: string;
  selectedItemId?: number;
  category?: string;
  region?: string;
}

type FoodTopicPost = FoodPost & {
  comment_section?: number;
  [key: string]: unknown;
};

type DietaryFlag = (typeof dietary)[number];

const HelmetComponent = Helmet as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
const MultiSliderComponent = MultiSlider as React.ComponentType<any>;
const MapContainerComponent = MapContainer as React.ComponentType<any>;
const TileLayerComponent = TileLayer as React.ComponentType<any>;

const FoodTopic = () => {
  const [foodData, setFoodData] = useState<FoodTopicPost[]>([]); // State to store search results from the database
  const { country } = useParams<{ country: string }>(); // Extract the country from the URL
  const arrayContainerRef = useRef<HTMLDivElement | null>(null); // Create a ref for the .array-container div
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]); // Ref for each item
  const [activePost, setActivePost] = useState(0); // Track the active post
  const [activeMarker, setActiveMarker] = useState(0); // State to store active marker index
  const [showMap, setShowMap] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedDietary, setSelectedDietary] = useState<DietaryFlag[]>([]);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation() as ReturnType<typeof useLocation> & { state?: FoodTopicLocationState };
  const locationState = location.state;

  const [currentCursor, setCurrentCursor] = useState('');
  const [oldCursor, setOldCursor] = useState('');
  const [newCursor, setNewCursor] = useState('');
  const [cursorToken, setCursorToken] = useState('');

  const [itemId, setItemId] = useState<number | null>(null);
  const [category, setCategory] = useState('');

  const navigate = useNavigate();
  const [mainIso, setMainIso] = useState('');
  const dietaryTabRef = useRef<HTMLDivElement | null>(null);
  const [availableCategories, setAvailableCategories] = useState<typeof foodTypes>(foodTypes);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [region, setRegion] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const setFoodDataTyped = (data: FoodPost[]) => {
    setFoodData(data as FoodTopicPost[]);
  };

  const handleItemClick = (post: FoodTopicPost, index: number, scrollToDescription = false) => {
    const [lat, lon] = ParseCoordinates(post.coordinates);
    localStorage.setItem('food_itemId', String(post.id));
    setActivePost(index); // Set the active post

    const dietFlags = dietary.filter((d) => post[d.id]);
    setSelectedDietary(dietFlags);

    if (!isNaN(lat) && !isNaN(lon)) {
      setMapCenter([lat, lon]);
      setActiveMarker(index);
      setShowMap(true);

      // Scroll the clicked item into view
      itemRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      setShowMap(false);
    }

    if (scrollToDescription) {
      descriptionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const openLightbox = (image?: string) => {
    if (!image) {
      return;
    }
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setSelectedImage('');
    setLightboxOpen(false);
  };

  const applyRegionFilter = (selectedRegion: string) => {
    if (!country) {
      return;
    }
    FetchCountryIso(country).then((iso) => {
      if (!iso) {
        return;
      }
      localStorage.removeItem('food_cursor');
      localStorage.removeItem('food_cursor_token');
      localStorage.removeItem('food_itemId');
      localStorage.setItem('food_region', selectedRegion);
      FetchFood(
        iso,
        { cursor: '', cursorToken: '', category, region: selectedRegion, selectedItemId: undefined },
        setOldCursor,
        setNewCursor,
        setCurrentCursor,
        setCursorToken,
        setCategory,
        setRegion,
        setCurrentPage,
        setFoodDataTyped,
        setMapCenter,
        setShowMap,
        setActivePost,
        setActiveMarker,
        itemRefs,
      );
      setItemId(foodData[activePost]?.id ?? null);
    });
  };

  useEffect(() => {
    if (!country) {
      return;
    }
    FetchCountryIso(country).then(async (iso) => {
      if (!iso) {
        return;
      }
      const [categoryRes, regionRes] = await Promise.all([
        axios.get(`${backendUrl}/worlddata/food/get-food-category-counts/${iso}/`),
        axios.get(`${backendUrl}/worlddata/food/get-food-region-counts/${iso}/${category ? `?food_category=${encodeURIComponent(category)}` : ''}`),
      ]);

      const counts = categoryRes.data as Array<{ food_category: string; count: number }>; // [{ food_category, count }]

      const valid = foodTypes.filter((p) => {
        const match = counts.find((c) => c.food_category === p.id);
        return match && match.count > 0;
      });

      setAvailableCategories(valid);

      const regions = (regionRes.data || []) as Array<{ region: string }>;
      setAvailableRegions(regions.map((r) => r.region));
    });
  }, [country, category]);

  useEffect(() => {
    if (!country) {
      return;
    }

    FetchCountryIso(country)
      .then((iso) => {
        if (!iso) {
          return;
        }
        setMainIso(iso);

        const savedItemId = localStorage.getItem('food_itemId');
        const savedCategory = localStorage.getItem('food_category');
        const savedRegion = localStorage.getItem('food_region');
        const hasIncomingCursor = Boolean(locationState?.selectedItemCursor);
        const requestedStartCursor = hasIncomingCursor ? (locationState?.selectedItemCursor ?? '') : '';
        const startCursorToken = hasIncomingCursor ? (locationState?.selectedItemCursorToken ?? '') : '';
        const startCursor = requestedStartCursor && !startCursorToken ? '' : requestedStartCursor;
        if (!hasIncomingCursor) {
          localStorage.removeItem('food_cursor');
          localStorage.removeItem('food_cursor_token');
        }
        if (requestedStartCursor && !startCursorToken) {
          localStorage.removeItem('food_cursor');
        }
        const startCategory = locationState?.category ?? savedCategory ?? '';
        const startRegion = locationState?.region ?? savedRegion ?? '';
        const startItemId = hasIncomingCursor ? (locationState?.selectedItemId ?? (savedItemId ? Number(savedItemId) : null) ?? null) : null;

        // Initialize history only if jumping
        setCurrentCursor(startCursor);
        setCursorToken(startCursorToken);
        setCategory(startCategory);
        setRegion(startRegion);
        setItemId(startItemId);

        FetchFood(
          iso,
          {
            cursor: startCursor,
            cursorToken: startCursorToken,
            category: startCategory,
            region: startRegion,
            selectedItemId: startItemId, // only used on first load
          },
          setOldCursor,
          setNewCursor,
          setCurrentCursor,
          setCursorToken,
          setCategory,
          setRegion,
          setCurrentPage,
          setFoodDataTyped,
          setMapCenter,
          setShowMap,
          setActivePost,
          setActiveMarker,
          itemRefs,
        ); // Fetch the correct page
      })
      .catch((error) => {
        console.error('Error fetching country iso:', error);
      });
  }, [country, locationState]); // Dependency array includes country and location state

  useEffect(() => {
    const sv1 = localStorage.getItem('food_cursor');
    if (sv1 !== null && sv1 !== '') {
      setCurrentCursor(sv1);
    }

    const sv2 = localStorage.getItem('food_category');
    if (sv2 !== null && sv2 !== '') {
      setCategory(sv2);
    }

    const sv3 = localStorage.getItem('food_itemId');
    if (sv3 !== null) {
      const parsedItemId = Number(sv3);
      if (Number.isFinite(parsedItemId)) {
        setItemId(parsedItemId);
      }
    }

    const sv4 = localStorage.getItem('food_region');
    if (sv4 !== null && sv4 !== '') {
      setRegion(sv4);
    }

    const sv5 = localStorage.getItem('food_cursor_token');
    if (sv5 !== null && sv5 !== '') {
      setCursorToken(sv5);
    }

    const arrayContainer = arrayContainerRef.current;

    // Call enableScroll and get the cleanup function
    const cleanupScroll = enableScroll(arrayContainer);

    // Cleanup scroll event listeners on component unmount
    return cleanupScroll;
  }, []); // Only runs once after the initial render

  useEffect(() => {
    if (currentCursor) {
      localStorage.setItem('food_cursor', currentCursor);
    } else {
      localStorage.removeItem('food_cursor');
    }
  }, [currentCursor]);

  useEffect(() => {
    if (cursorToken) {
      localStorage.setItem('food_cursor_token', cursorToken);
    } else {
      localStorage.removeItem('food_cursor_token');
    }
  }, [cursorToken]);

  useEffect(() => {
    if (itemId) {
      localStorage.setItem('food_itemId', String(itemId));
    }
  }, [itemId]);

  useEffect(() => {
    if (category !== undefined && category !== null) {
      localStorage.setItem('food_category', category);
    }
  }, [category]);

  useEffect(() => {
    if (region !== undefined && region !== null) {
      localStorage.setItem('food_region', region);
    }
  }, [region]);

  useEffect(() => {
    if (locationState) {
      window.history.replaceState({}, document.title);
    }
  }, [locationState]);

  useEffect(() => {
    if (Array.isArray(foodData) && foodData.length > 0) {
      const post = foodData[activePost];
      const dietFlags = dietary.filter((d) => post[d.id]);
      setSelectedDietary(dietFlags);
    }
  }, [foodData, activePost]);

  const selectedCategoryLabel = category ? availableCategories.find((c) => c.id === category)?.label || category : 'ALL';

  const encodedCountry = encodeURIComponent(country || '');
  const canonicalUrl = `https://livingoutofabackpack.com/traveltopics/${encodedCountry}/topics/food`;
  const socialImage = foodData?.[activePost]?.images?.[0]
    ? `${backendUrl}/django-dynamic/pictures/${foodData[activePost].images[0]}`
    : 'https://livingoutofabackpack.com/static/logo.jpg';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://livingoutofabackpack.com/home/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Travel Topics',
        item: 'https://livingoutofabackpack.com/traveltopics/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${country} Food`,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div className="FoodPage">
      <HelmetComponent>
        <title>Best Food in {country} | Restaurants & Local Cuisine</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content={`Discover local food in ${country}, from popular dishes to recommended restaurants and cuisine highlights.`} />
        <meta
          name="keywords"
          content={`${country} food spots, 
                  cuisine in ${country},
                  top restaurants in ${country},
                  best restaurants to visit in ${country}`}
        />
        <meta property="og:title" content={`Best Food in ${country} | Restaurants & Local Cuisine`} />
        <meta property="og:description" content={`Explore restaurant picks and must-try local dishes in ${country}.`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:alt" content={`Food highlights from ${country}`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </HelmetComponent>

      <h1>
        Food from {country}
        <span className="iso-badge">
          {mainIso && (
            <img
              src={`https://flagcdn.com/w320/${mainIso.toLowerCase()}.png`}
              alt={`${mainIso} flag`}
              style={{
                width: '100%',
                maxWidth: '20px',
                height: 'auto',
                borderRadius: '4px',
              }}
            />
          )}
        </span>
      </h1>

      <h2>Enjoy the local delicacies of {country}.</h2>

      <div className="array-container" ref={arrayContainerRef}>
        {Array.isArray(foodData) && foodData.length > 0 ? (
          foodData.map((post, index) => (
            <div key={post.id} className={`array-item ${activePost === index ? 'active' : ''}`} onClick={() => handleItemClick(post, index)} ref={(el) => { itemRefs.current[index] = el; }}>
              <div className="picture-content">
                <div className="image-container">
                  <img src={`${backendUrl}/django-dynamic/pictures/${post.images.at(0)}`} title={post.dish || post.restaurant} alt={post.dish || post.restaurant || 'Food post'} onClick={() => openLightbox(post.images.at(0))} />

                  {post.attributions?.[0] && (
                    <div className="attribution">
                      <div className="attribution-content">
                        {IsHtml(post.attributions[0]) ? <div dangerouslySetInnerHTML={{ __html: post.attributions[0] }} /> : <span>{post.attributions[0]}</span>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="block-title">
                {post.dish} {post.restaurant}
              </div>

              <div className="DietaryIcons">
                {dietary.map((d) =>
                  post[d.id] ? (
                    <span
                      key={d.id}
                      className="DietaryEmoji"
                      title={d.explanation}
                      onClick={(e) => {
                        e.stopPropagation(); // prevent triggering the card click
                        // update dietary info for this post
                        const dietFlags = dietary.filter((flag) => post[flag.id]);
                        setSelectedDietary(dietFlags);
                        // scroll to tab
                        dietaryTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }}
                    >
                      {d.emoji}
                    </span>
                  ) : null,
                )}
              </div>

              <div className="quick-select">
                {post.coordinates && <div className="coordinates">{post.coordinates}</div>}

                <button
                  className="description-button"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the array-item click
                    handleItemClick(post, index, true); // Scroll to the description
                  }}
                >
                  Description
                </button>
              </div>
            </div>
          ))
        ) : (
          <p>Dishes and restaurants.</p>
        )}
      </div>

      <div className="filter-nav-row">
        <section className="filter-section" aria-label="Filter">
          <h3 className="filter-section-title">Filter</h3>
          <div className="filter-controls">
            <div className="category-filter-wrap">
              <label className="region-filter-label">Category Filter</label>
              <MultiSliderComponent
                type={'food'}
                options={availableCategories}
                onChange={(option: any) => {
                  if (!country) {
                    return;
                  }
                  FetchCountryIso(country).then((iso) => {
                    if (!iso) {
                      return;
                    }
                    const cat = option === 'ALL' ? '' : option.id;
                    localStorage.removeItem('food_cursor');
                    localStorage.removeItem('food_cursor_token');
                    localStorage.removeItem('food_category');
                    localStorage.removeItem('food_itemId');
                    localStorage.removeItem('food_region');
                    FetchFood(
                      iso,
                      { cursor: '', cursorToken: '', category: cat, region: '', selectedItemId: undefined },
                      setOldCursor,
                      setNewCursor,
                      setCurrentCursor,
                      setCursorToken,
                      setCategory,
                      setRegion,
                      setCurrentPage,
                      setFoodDataTyped,
                      setMapCenter,
                      setShowMap,
                      setActivePost,
                      setActiveMarker,
                      itemRefs,
                    );
                    setItemId(foodData[activePost]?.id ?? null);
                  });
                }}
              />
            </div>

            <div className="region-filter-wrap">
              <label htmlFor="region-filter" className="region-filter-label">
                Region Filter
              </label>
              <select
                id="region-filter"
                className="region-filter-select"
                value={region}
                onChange={(e) => applyRegionFilter(e.target.value)}
              >
                <option value="">ALL</option>
                {availableRegions.map((regionName) => (
                  <option key={regionName} value={regionName}>
                    {regionName}
                  </option>
                ))}
              </select>
              <div className="region-filter-hint">Regions shown for category: {selectedCategoryLabel}</div>
            </div>
          </div>
        </section>

        <div className="nav-controls">
          <div className="Pagination">
            <button
              className="PaginationButtonLeft"
              disabled={!newCursor}
              onClick={() => {
                if (!country) {
                  return;
                }
                FetchCountryIso(country).then((iso) => {
                  if (!iso) {
                    return;
                  }
                  FetchFood(
                    iso,
                    { cursor: newCursor, cursorToken, category, region, selectedItemId: undefined },
                    setOldCursor,
                    setNewCursor,
                    setCurrentCursor,
                    setCursorToken,
                    setCategory,
                    setRegion,
                    setCurrentPage,
                    setFoodDataTyped,
                    setMapCenter,
                    setShowMap,
                    setActivePost,
                    setActiveMarker,
                    itemRefs,
                  );
                });
              }}
            >
              <span className="material-icons">chevron_left</span>
            </button>

            <span className="PageIndicator">
              <div className="Title">Food Posts</div>
              Page Number {currentPage}
            </span>

            <button
              className="PaginationButtonRight"
              disabled={!oldCursor}
              onClick={() => {
                if (!country) {
                  return;
                }
                FetchCountryIso(country).then((iso) => {
                  if (!iso) {
                    return;
                  }
                  FetchFood(
                    iso,
                    { cursor: oldCursor, cursorToken, category, region, selectedItemId: undefined },
                    setOldCursor,
                    setNewCursor,
                    setCurrentCursor,
                    setCursorToken,
                    setCategory,
                    setRegion,
                    setCurrentPage,
                    setFoodDataTyped,
                    setMapCenter,
                    setShowMap,
                    setActivePost,
                    setActiveMarker,
                    itemRefs,
                  );
                });
              }}
            >
              <span className="material-icons">chevron_right</span>
            </button>
          </div>

          {foodData.length > 0 && foodData[activePost] && (
            <button
              className="report-btn"
              onClick={(e) => {
                e.stopPropagation(); // prevent triggering the card click
                navigate(
                  `/reportpage?itemSearch=${encodeURIComponent(foodData[activePost].dish || foodData[activePost].restaurant)}` +
                    `&category=food` +
                    `&id=${foodData[activePost].id}` +
                    `&iso=${foodData[activePost].iso}` +
                    `&country=${country}`,
                );
              }}
            >
              <span className="material-icons">flag</span>
              Report
            </button>
          )}
        </div>
      </div>

      <section className="content-section" aria-label="Content">
        <h3 className="content-section-title">Content</h3>
      <div className="PostWiki">
        {showMap && (
          <div className="LeafletContainer">
            <div className="map-gesture-hint">Scroll normally. Hold on map or use two fingers to drag.</div>
            <MapContainerComponent center={mapCenter} zoom={7} style={{ height: '300px', width: '300px' }}>
              <MapGestureControl />
              <TileLayerComponent url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
              <UpdateMapView center={mapCenter} />
              {foodData.map((post, index) => {
                const [lat, lon] = ParseCoordinates(post.coordinates);
                if (!isNaN(lat) && !isNaN(lon)) {
                  return (
                    <MemoizedMarker
                      key={index}
                      position={[lat, lon]}
                      icon={CreateCustomIcon(activeMarker === index)}
                      onClick={() => HandleMarkerItemClick(index, setActiveMarker, setActivePost, itemRefs)}
                    />
                  );
                }
                return null;
              })}
            </MapContainerComponent>
          </div>
        )}

        <div className="Description" ref={descriptionRef}>
          <div className="DescriptionTitleBanner">
            <h1> {foodData.length > 0 && foodData[activePost] ? foodData[activePost].dish || foodData[activePost].restaurant : 'Food name.'}</h1>
            <h2>Description</h2>
          </div>
          <div className="DescriptionText">{foodData.length > 0 && foodData[activePost] ? foodData[activePost].description : 'Food description.'}</div>
        </div>

        {selectedDietary.length > 0 && (
          <div className="DietaryTab" ref={dietaryTabRef}>
            <div className="DietaryTitleBanner">
              <h2>Dietary Info</h2>
            </div>
            <ul>
              {selectedDietary.map((d, idx) => (
                <li key={idx}>
                  <span className="DietaryEmoji">{d.emoji}</span>
                  <div>
                    <div className="DietaryLabel">{d.label}</div>
                    <div className="DietaryExplanation">{d.explanation}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {foodData.length > 0 && foodData[activePost] && foodData[activePost].images
          ? foodData[activePost].images.slice(1).map((image, index) => (
              <div className="ExtraPictures">
                <div className="picture-content">
                  <div className="image-container">
                    <img
                      key={index}
                      src={`${backendUrl}/django-dynamic/pictures/${image}`}
                      title={`${foodData[activePost].dish} ${foodData[activePost].restaurant} - Extra Picture ${index + 1}`}
                      alt={`${foodData[activePost].dish} ${foodData[activePost].restaurant} - Extra ${index + 1}`}
                    />

                    {foodData[activePost].attributions?.[0] && (
                      <div className="attribution">
                        <div className="attribution-content">
                          {IsHtml(foodData[activePost].attributions[0]) ? (
                            <div dangerouslySetInnerHTML={{ __html: foodData[activePost].attributions[0] }} />
                          ) : (
                            <span>{foodData[activePost].attributions[0]}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          : null}

        {foodData.length > 0 && foodData[activePost] && foodData[activePost].coordinates && <LocalClock coordinates={foodData[activePost].coordinates} />}
      </div>
      </section>

      {lightboxOpen && (
        <div className="lightbox" onClick={closeLightbox}>
          <img src={`${backendUrl}/django-dynamic/pictures/${selectedImage}`} alt="Enlarged view" />
          <button className="close-button" onClick={closeLightbox}>
            X
          </button>
        </div>
      )}

      {foodData.length > 0 && foodData[activePost] && typeof foodData[activePost].comment_section === 'number' && (
        <CommentSection sectionId={foodData[activePost].comment_section as number} />
      )}
    </div>
  );
};

export default FoodTopic;
